/**
 * Session Result Router
 *
 * Maps the output_form produced by a method variant to a concrete persistence
 * target, then provides typed helpers to build the right payload for each target.
 *
 * This extends the coarser toolResultNormalization.ts (which treats all tool
 * outputs identically) with a form-aware, S-class-aware routing layer.
 *
 * Persistence targets:
 *  session_results   — row(s) in session_results table (rank + score + meta)
 *  item_pool_update  — updates item pool membership / locking
 *  criteria_artifact — writes a criteria_locked artifact
 *  narrative_artifact — stores a text/narrative output in session_results.notes
 */

import type { DecisionClass } from '../types/prioritization/session';
import type { OutputForm } from './methodRegistry';
import type { ToolResultItem } from '../components/prioritization/ToolWrapper';
import {
  buildSessionScoreRecord,
  buildSessionScores,
  buildSessionClassifications,
} from './toolResultNormalization';
import type { ResultDestination } from './sessionLauncherEngine';
import { deriveResultDestinations } from './sessionLauncherEngine';

// ============================================================================
// Types
// ============================================================================

/** A fully-routed result ready to hand off to stores / API callers */
export interface RoutedResult {
  /** Where this result should be persisted */
  destination: ResultDestination;
  /** Payload for session_results rows */
  sessionResultsPayload?: SessionResultsPayload;
  /** Payload for item pool updates */
  itemPoolPayload?: ItemPoolUpdatePayload;
  /** Payload for criteria artifact */
  criteriaPayload?: CriteriaArtifactPayload;
  /** Payload for narrative artifact */
  narrativePayload?: NarrativeArtifactPayload;
}

export interface SessionResultsPayload {
  /** Map of itemId → scores (uses existing buildSessionScores shape) */
  scores: Record<string, Record<string, number>>;
  /** Map of itemId → classifications (category, tier, quadrant, etc.) */
  classifications: Record<string, Record<string, string>>;
  /** Ordered list of item IDs for rank metadata */
  rankedItemIds: string[];
  /** The output form that produced these results */
  outputForm: OutputForm;
  /** The S-class this session ran under */
  decisionClass: DecisionClass;
}

export interface ItemPoolUpdatePayload {
  /** Item IDs to include in the locked pool */
  includedItemIds: string[];
  /** Item IDs to exclude / remove from pool (if any) */
  excludedItemIds: string[];
  /** Tier or category metadata for each item */
  tierMetadata?: Record<string, string>;
}

export interface CriteriaArtifactPayload {
  /** Attribute / criterion IDs selected */
  selectedCriteriaIds: string[];
  notes?: string;
}

export interface NarrativeArtifactPayload {
  title: string;
  content: string;
  /** The output form that generated this narrative */
  outputForm: OutputForm;
}

// ============================================================================
// Routing logic
// ============================================================================

/**
 * Route tool results based on the output_form and decision class.
 *
 * Returns one RoutedResult per destination. The SessionLauncher or ToolWrapper
 * completion handler calls the appropriate store action for each.
 */
export function routeToolResults(
  results: ToolResultItem[],
  outputForm: OutputForm,
  decisionClass: DecisionClass,
  narrativeContent?: string,
): RoutedResult[] {
  const destinations = deriveResultDestinations([outputForm], decisionClass);
  const routed: RoutedResult[] = [];

  for (const dest of destinations) {
    switch (dest) {
      case 'session_results':
        routed.push({
          destination: 'session_results',
          sessionResultsPayload: buildSessionResultsPayload(results, outputForm, decisionClass),
        });
        break;

      case 'item_pool_update':
        routed.push({
          destination: 'item_pool_update',
          itemPoolPayload: buildItemPoolPayload(results, outputForm),
        });
        break;

      case 'criteria_artifact':
        routed.push({
          destination: 'criteria_artifact',
          criteriaPayload: {
            selectedCriteriaIds: results.map((r) => r.id),
            notes: narrativeContent,
          },
        });
        break;

      case 'narrative_artifact':
        routed.push({
          destination: 'narrative_artifact',
          narrativePayload: {
            title: `${outputForm} result`,
            content: narrativeContent ?? formatResultsAsNarrative(results),
            outputForm,
          },
        });
        break;
    }
  }

  return routed;
}

// ============================================================================
// Payload builders
// ============================================================================

function buildSessionResultsPayload(
  results: ToolResultItem[],
  outputForm: OutputForm,
  decisionClass: DecisionClass,
): SessionResultsPayload {
  return {
    scores: buildSessionScores(results),
    classifications: buildSessionClassifications(results),
    rankedItemIds: results
      .slice()
      .sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999))
      .map((r) => r.id),
    outputForm,
    decisionClass,
  };
}

function buildItemPoolPayload(
  results: ToolResultItem[],
  outputForm: OutputForm,
): ItemPoolUpdatePayload {
  if (outputForm === 'tiered_list') {
    // For tiered output (MoSCoW, Max Priorities, etc.):
    // Items in "include" tiers go into the pool; others are excluded
    const INCLUDE_TIERS = new Set(['must', 'should', 'ui', 'high', 'top', 'include', 'in']);
    const EXCLUDE_TIERS = new Set(['wont', 'could', 'low', 'exclude', 'out', 'nn']);

    const included: string[] = [];
    const excluded: string[] = [];
    const tierMeta: Record<string, string> = {};

    for (const r of results) {
      const tier = r.category?.toLowerCase() ?? '';
      tierMeta[r.id] = r.category ?? '';
      if (INCLUDE_TIERS.has(tier)) included.push(r.id);
      else if (EXCLUDE_TIERS.has(tier)) excluded.push(r.id);
      else included.push(r.id); // default: include if uncertain
    }

    return { includedItemIds: included, excludedItemIds: excluded, tierMetadata: tierMeta };
  }

  if (outputForm === 'allocation_distribution') {
    // For allocation (Buy a Feature, Speed Boat quant):
    // Items that received any allocation are included
    const included = results.filter((r) => (r.score ?? 0) > 0).map((r) => r.id);
    const excluded = results.filter((r) => (r.score ?? 0) === 0).map((r) => r.id);
    return { includedItemIds: included, excludedItemIds: excluded };
  }

  // Default: include all
  return {
    includedItemIds: results.map((r) => r.id),
    excludedItemIds: [],
  };
}

function formatResultsAsNarrative(results: ToolResultItem[]): string {
  return results
    .map((r, i) => `${i + 1}. ${r.name}${r.category ? ` [${r.category}]` : ''}`)
    .join('\n');
}

// ============================================================================
// Re-export for convenience
// ============================================================================

export { buildSessionScores, buildSessionScoreRecord, buildSessionClassifications };

/**
 * Given a set of output forms and an S-class, return a human-readable summary
 * of where results will be stored — for use in the launcher confirmation step.
 */
export function describeResultDestinations(
  outputForms: OutputForm[],
  sClass: DecisionClass,
): string {
  const destinations = deriveResultDestinations(outputForms, sClass);

  const labels: Record<ResultDestination, string> = {
    session_results: 'Session results',
    item_pool_update: 'Item pool update',
    criteria_artifact: 'Criteria set artifact',
    narrative_artifact: 'Narrative record',
  };

  return destinations.map((d) => labels[d]).join(' + ');
}
